/**
 * Business onboarding, in three steps.
 *
 * Three rather than one because the form grew from four fields to eleven, and
 * eleven fields on a single screen is where signups are abandoned. Three
 * rather than five because every extra "Next" is another chance to leave.
 *
 * Only the first step has required fields. Everything in steps two and three
 * has a defensible default, is skippable, and is editable afterwards in
 * Settings — so a founder in a hurry can be trading in under a minute, and one
 * who wants to set up properly can do it here.
 */

import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Store } from 'lucide-react';
import {
  moduleDefaultFor,
  COUNTRIES,
  CURRENCIES,
  OFFERING_KINDS,
  OFFERING_KIND_LABELS,
  SHOP_PRESENCES,
  SHOP_PRESENCE_LABELS,
  findCountry,
  onboardingChoices,
  templateForPreset,
  type Business,
  type BusinessType,
  type OfferingKind,
  type ShopPresence,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button, Input, Select, cn } from '../../components/ui';
import { AuthLayout } from '../auth/AuthLayout';

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: c.name }));
const CURRENCY_OPTIONS = CURRENCIES.map((c) => ({
  value: c.code,
  label: `${c.code} — ${c.name}`,
}));

const CHOICES = onboardingChoices();

const STEPS = ['Your business', 'How you operate', 'Set up your shop'] as const;

export function OnboardingPage(): ReactNode {
  const { user, loading, reload, selectBusiness } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);

  // Step 1
  const [name, setName] = useState('');
  const [choice, setChoice] = useState<string>('supermarket_retail');
  const [countryCode, setCountryCode] = useState('NG');
  const [currencyCode, setCurrencyCode] = useState('NGN');
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Step 2
  const [offeringKind, setOfferingKind] = useState<OfferingKind>('both');
  const [offeringTouched, setOfferingTouched] = useState(false);
  const [shopPresence, setShopPresence] = useState<ShopPresence>('physical');
  const [address, setAddress] = useState('');

  // Step 3
  const [mainLocationName, setMainLocationName] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const selected = CHOICES.find((c) => c.value === choice) ?? CHOICES[0]!;
  const template = useMemo(
    () => templateForPreset(selected.type as BusinessType, selected.presetSlug),
    [selected],
  );

  useEffect(() => {
    if (!loading && !user) navigate('/login?next=%2Fbusiness%2Fonboarding', { replace: true });
  }, [loading, user, navigate]);

  // Selecting a country pre-fills currency, until the founder overrides it —
  // a Nigerian business billing in USD is common enough to support, and
  // silently changing it back would be worse than not helping at all.
  useEffect(() => {
    if (currencyTouched) return;
    const country = findCountry(countryCode);
    if (country) setCurrencyCode(country.currencyCode);
  }, [countryCode, currencyTouched]);

  // The business type knows what it usually sells. Still overridable.
  useEffect(() => {
    if (offeringTouched) return;
    setOfferingKind(template.defaultOffering);
  }, [template, offeringTouched]);

  const canAdvance = step > 0 || name.trim().length > 0;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();

    if (step < STEPS.length - 1) {
      setStep((current) => current + 1);
      return;
    }

    setError(null);
    setFieldErrors({});
    setSubmitting(true);

    try {
      const business = await api.post<Business>('/businesses', {
        name: name.trim(),
        type: selected.type,
        presetSlug: selected.presetSlug,
        countryCode,
        currencyCode,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        address: address.trim() || undefined,
        offeringKind,
        shopPresence,
        mainLocationName: mainLocationName.trim() || undefined,
      });

      await reload();
      selectBusiness(business.id);
      /*
        Where the first useful action lives for this KIND of business: the
        till for a business that sells from a catalogue, the dashboard — with
        Deliveries beside it — for one whose type starts with Inventory off,
        such as Delivery & Logistics. Read from the same defaults the server
        uses, so the two cannot disagree.
      */
      navigate(
        moduleDefaultFor(selected.type, 'inventory') ? '/shop?welcome=1' : '/business',
        { replace: true },
      );
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? '']),
            ),
          );
          // Send them back to the step that actually holds the bad field.
          if (caught.fields.name || caught.fields.countryCode || caught.fields.currencyCode) {
            setStep(0);
          }
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Set up your business"
      subtitle="Three quick steps. You can change any of this later."
    >
      <ol className="mb-6 flex items-center gap-2" aria-label="Progress">
        {STEPS.map((label, index) => (
          <li key={label} className="flex flex-1 items-center gap-2">
            <span
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                index < step
                  ? 'bg-emerald-600 text-white'
                  : index === step
                    ? 'bg-brand-600 text-white'
                    : 'bg-slate-200 text-slate-500',
              )}
            >
              {index < step ? <Check className="size-3.5" aria-hidden /> : index + 1}
            </span>
            <span
              className={cn(
                'hidden truncate text-xs sm:block',
                index === step ? 'font-medium text-slate-900' : 'text-slate-500',
              )}
            >
              {label}
            </span>
          </li>
        ))}
      </ol>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {step === 0 && (
          <>
            <Input
              label="Business name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={fieldErrors.name}
              placeholder="e.g. Ada Fabrics"
              required
              autoFocus
            />
            <Select
              label="What kind of business is it?"
              options={CHOICES.map((c) => ({ value: c.value, label: c.label }))}
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              hint={`${selected.hint} This can’t be changed after the business is created.`}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Country"
                options={COUNTRY_OPTIONS}
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                error={fieldErrors.countryCode}
              />
              <Select
                label="Currency"
                options={CURRENCY_OPTIONS}
                value={currencyCode}
                onChange={(e) => {
                  setCurrencyTouched(true);
                  setCurrencyCode(e.target.value);
                }}
                error={fieldErrors.currencyCode}
                hint="Every figure is recorded in this currency"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Phone (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                error={fieldErrors.phone}
                placeholder="+234 800 000 0000"
              />
              <Input
                label="Business email (optional)"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={fieldErrors.email}
              />
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-slate-700">
                What do you sell?
              </legend>
              <div className="space-y-2">
                {OFFERING_KINDS.map((value) => (
                  <ChoiceCard
                    key={value}
                    checked={offeringKind === value}
                    onSelect={() => {
                      setOfferingTouched(true);
                      setOfferingKind(value);
                    }}
                    label={OFFERING_KIND_LABELS[value]}
                  />
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className="mb-2 text-sm font-medium text-slate-700">
                Where do you sell?
              </legend>
              <div className="space-y-2">
                {SHOP_PRESENCES.map((value) => (
                  <ChoiceCard
                    key={value}
                    checked={shopPresence === value}
                    onSelect={() => setShopPresence(value)}
                    label={SHOP_PRESENCE_LABELS[value]}
                  />
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                This decides which sales channels start switched on. You can change them any
                time.
              </p>
            </fieldset>

            <Input
              label="Main business location (optional)"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="12 Balogun Street, Lagos"
              hint="Used on reports and receipts"
            />
          </>
        )}

        {step === 2 && (
          <>
            <Input
              label="Name your first branch (optional)"
              value={mainLocationName}
              onChange={(e) => setMainLocationName(e.target.value)}
              placeholder="Main"
              hint="Most businesses start with one. Add more later from Settings."
            />

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <Store className="size-4 text-brand-600" aria-hidden />
                We&rsquo;ll set these up for you
              </p>
              <ul className="mt-2 space-y-1 text-sm text-slate-600">
                <li>
                  <strong className="font-medium text-slate-700">
                    {template.catalogueNoun.plural}
                  </strong>{' '}
                  organised into {template.productCategories.length} categories:{' '}
                  {template.productCategories.slice(0, 4).join(', ')}
                  {template.productCategories.length > 4 ? '…' : ''}
                </li>
                <li>Expense categories suited to a {selected.label.toLowerCase()}</li>
                <li>Sales channels matching how you sell</li>
              </ul>
              <p className="mt-2 text-xs text-slate-500">
                All of it is yours to rename or remove — these are just a starting point.
              </p>
            </div>

            {/*
              A logo upload belongs here, but it needs a business to attach to
              and the business does not exist until this form is submitted.
              Adding it from Settings immediately after avoids a two-phase
              upload that would orphan files whenever onboarding is abandoned.
            */}
            <p className="text-xs text-slate-500">
              You can add your logo from Settings once your business is created.
            </p>
          </>
        )}

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        <div className="flex items-center gap-2 pt-2">
          {step > 0 && (
            <Button
              type="button"
              variant="secondary"
              icon={<ArrowLeft className="size-4" />}
              onClick={() => setStep((current) => current - 1)}
              disabled={submitting}
            >
              Back
            </Button>
          )}
          <Button
            type="submit"
            className="flex-1"
            disabled={!canAdvance || submitting}
            icon={step === STEPS.length - 1 ? undefined : <ArrowRight className="size-4" />}
          >
            {submitting
              ? 'Creating your business…'
              : step === STEPS.length - 1
                ? 'Create business'
                : 'Continue'}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}

function ChoiceCard({
  checked,
  onSelect,
  label,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={checked}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left text-sm transition-colors',
        checked
          ? 'border-brand-500 bg-brand-50 text-brand-900'
          : 'border-slate-200 text-slate-700 hover:bg-slate-50',
      )}
    >
      <span
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded-full border',
          checked ? 'border-brand-600 bg-brand-600' : 'border-slate-300',
        )}
        aria-hidden
      >
        {checked && <span className="size-1.5 rounded-full bg-white" />}
      </span>
      {label}
    </button>
  );
}
